/**
 * Signature spec — the single source of truth for a Gmail signature.
 *
 * Users and FutureBot edit this JSON structure (never raw HTML); the
 * deterministic compiler (compileSignatureHtml.js) turns it into Gmail-safe
 * table-based HTML. Structure:
 *
 *   { version, name, global, rows: [ { id, spacingTop, columns: [ { id, width?,
 *     valign, align, paddingLeft, paddingRight, borderLeft?, blocks: [...] } ] } ] }
 *
 * Block types: image | text | contact | social | button | divider | spacer | disclaimer.
 *
 * NOTE: the FutureBot server-side validator
 * (functions/FutureBot/validateSignatureTools.js) mirrors these constants —
 * keep the two in sync when ranges/enums change.
 */

export const SIGNATURE_CHAR_LIMIT = 10000;

export const WEB_SAFE_FONTS = [
  { id: 'arial', label: 'Arial', stack: 'Arial, Helvetica, sans-serif' },
  { id: 'helvetica', label: 'Helvetica', stack: 'Helvetica, Arial, sans-serif' },
  { id: 'georgia', label: 'Georgia', stack: 'Georgia, Times, serif' },
  { id: 'verdana', label: 'Verdana', stack: 'Verdana, Geneva, sans-serif' },
  { id: 'tahoma', label: 'Tahoma', stack: 'Tahoma, Geneva, sans-serif' },
  { id: 'trebuchet', label: 'Trebuchet MS', stack: "'Trebuchet MS', Helvetica, sans-serif" },
  { id: 'times', label: 'Times New Roman', stack: "'Times New Roman', Times, serif" },
  { id: 'courier', label: 'Courier New', stack: "'Courier New', Courier, monospace" },
];

export const FONT_BY_ID = WEB_SAFE_FONTS.reduce((acc, f) => {
  acc[f.id] = f;
  return acc;
}, {});

export const BLOCK_TYPES = ['image', 'text', 'contact', 'social', 'button', 'divider', 'spacer', 'disclaimer'];

export const CONTACT_KINDS = ['phone', 'email', 'website', 'text', 'link'];

export const SOCIAL_NETWORKS = [
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'x', label: 'X' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'custom', label: 'Custom' },
];

export const LIMITS = {
  fontSize: { min: 9, max: 24 },
  maxWidth: { min: 280, max: 600 },
  imageSize: { min: 12, max: 600 },
  spacing: { min: 0, max: 60 },
  lineHeight: { min: 10, max: 40 },
  iconSize: { min: 12, max: 48 },
  radius: { min: 0, max: 24 },
  thickness: { min: 1, max: 6 },
  textLength: 500,
  maxRows: 8,
  maxColumnsPerRow: 4,
  maxBlocksPerColumn: 16,
  maxContactItems: 8,
  maxSocialIcons: 6,
  maxSegments: 24,
  segmentTextLength: 300,
  letterSpacing: { min: 1, max: 10 },
};

// Imported signatures may legitimately contain tiny images (icons, spacer
// gifs), so sanitization accepts down to 1px even though the editor sliders
// start at LIMITS.imageSize.min.
const IMAGE_SANITIZE_MIN = 1;

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export function isHexColor(value) {
  return typeof value === 'string' && HEX_RE.test(value);
}

export function clamp(value, min, max, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

/**
 * Normalize a user/model-supplied link into a safe href.
 * Returns '' when the value can't be made safe (caller renders plain text).
 */
export function normalizeHref(kind, value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (kind === 'phone') {
    const digits = raw.replace(/[^\d+]/g, '');
    if (!digits.replace(/\D/g, '').length) return '';
    const withCountry = digits.startsWith('+') ? digits : `+1${digits.replace(/\D/g, '')}`;
    return `tel:${withCountry}`;
  }
  if (kind === 'email') {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) return '';
    return `mailto:${raw}`;
  }
  // website / link / custom URLs
  if (/^javascript:/i.test(raw) || /^data:/i.test(raw) || /^vbscript:/i.test(raw)) return '';
  if (/^(https?:\/\/|mailto:|tel:)/i.test(raw)) return raw;
  if (/^\/\/[\w.-]+/.test(raw)) return `https:${raw}`; // protocol-relative
  if (/^[\w.-]+\.[a-z]{2,}([/?#].*)?$/i.test(raw)) return `https://${raw}`;
  return '';
}

/** Only https (or protocol-relative-upgraded) URLs are allowed for images. */
export function normalizeImageSrc(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^https:\/\//i.test(raw)) return raw;
  if (/^http:\/\//i.test(raw)) return raw.replace(/^http:/i, 'https:');
  return '';
}

// ---------------------------------------------------------------------------
// Ids
// ---------------------------------------------------------------------------

function collectIds(spec) {
  const ids = new Set();
  (spec.rows || []).forEach((row) => {
    ids.add(row.id);
    (row.columns || []).forEach((col) => {
      ids.add(col.id);
      (col.blocks || []).forEach((b) => ids.add(b.id));
    });
  });
  return ids;
}

export function generateId(prefix, spec) {
  const ids = collectIds(spec);
  let n = 1;
  while (ids.has(`${prefix}${n}`)) n += 1;
  return `${prefix}${n}`;
}

export function cloneSpec(spec) {
  return JSON.parse(JSON.stringify(spec));
}

// ---------------------------------------------------------------------------
// Factories
// ---------------------------------------------------------------------------

export function defaultGlobal() {
  return {
    fontId: 'arial',
    fontSize: 13,
    textColor: '#333333',
    linkColor: '#1a6dad',
    accentColor: '#0f4c81',
    maxWidth: 520,
  };
}

export function createBlock(type, overrides = {}) {
  const base = { type };
  switch (type) {
    case 'image':
      return { ...base, src: '', alt: 'Image', width: 96, height: 96, circle: false, href: '', align: 'left', spacingTop: 0, ...overrides };
    case 'text':
      return { ...base, text: '', size: null, color: '', bold: false, italic: false, underline: false, lineHeight: null, href: '', spacingTop: 0, ...overrides };
    case 'contact':
      return { ...base, items: [], separator: ' | ', layout: 'inline', size: null, color: '', spacingTop: 4, ...overrides };
    case 'social':
      return { ...base, icons: [], size: 20, gap: 8, spacingTop: 8, ...overrides };
    case 'button':
      return { ...base, text: 'Apply Now', href: '', bg: '#0f4c81', color: '#ffffff', radius: 4, paddingX: 14, paddingY: 8, size: 13, align: 'left', spacingTop: 10, ...overrides };
    case 'divider':
      return { ...base, color: '#dddddd', thickness: 1, spacingTop: 8, spacingBottom: 8, ...overrides };
    case 'spacer':
      return { ...base, height: 8, ...overrides };
    case 'disclaimer':
      return { ...base, text: '', size: 10, color: '#999999', lineHeight: null, spacingTop: 8, ...overrides };
    default:
      return { ...base, ...overrides };
  }
}

export function createColumn(overrides = {}) {
  return {
    width: null,
    valign: 'top',
    align: 'left',
    paddingLeft: 0,
    paddingRight: 0,
    borderLeft: null, // { color, thickness }
    blocks: [],
    ...overrides,
  };
}

export function createRow(overrides = {}) {
  return { spacingTop: 0, columns: [], ...overrides };
}

// ---------------------------------------------------------------------------
// Spec lookups
// ---------------------------------------------------------------------------

export function findBlock(spec, blockId) {
  for (const row of spec.rows || []) {
    for (const col of row.columns || []) {
      const idx = (col.blocks || []).findIndex((b) => b.id === blockId);
      if (idx !== -1) return { block: col.blocks[idx], column: col, row, index: idx };
    }
  }
  return null;
}

export function findColumn(spec, columnId) {
  for (const row of spec.rows || []) {
    const col = (row.columns || []).find((c) => c.id === columnId);
    if (col) return { column: col, row };
  }
  return null;
}

export function findRow(spec, rowId) {
  return (spec.rows || []).find((r) => r.id === rowId) || null;
}

/** Last column of the last row — the default target for appended blocks. */
function defaultColumn(spec) {
  const rows = spec.rows || [];
  if (!rows.length) return null;
  const cols = rows[rows.length - 1].columns || [];
  return cols.length ? cols[cols.length - 1] : null;
}

// ---------------------------------------------------------------------------
// Mutation helpers — shared by the manual editor and the FutureBot tools.
// Each returns { ok: true, spec, ...info } or { ok: false, error } and never
// mutates the input spec.
// ---------------------------------------------------------------------------

export function updateGlobalStyle(spec, patch = {}) {
  const next = cloneSpec(spec);
  const g = next.global;
  if (patch.fontId !== undefined) {
    if (!FONT_BY_ID[patch.fontId]) return { ok: false, error: `Unknown fontId "${patch.fontId}". Valid: ${WEB_SAFE_FONTS.map((f) => f.id).join(', ')}` };
    g.fontId = patch.fontId;
  }
  if (patch.fontSize !== undefined) g.fontSize = clamp(patch.fontSize, LIMITS.fontSize.min, LIMITS.fontSize.max, g.fontSize);
  if (patch.maxWidth !== undefined) g.maxWidth = clamp(patch.maxWidth, LIMITS.maxWidth.min, LIMITS.maxWidth.max, g.maxWidth);
  for (const key of ['textColor', 'linkColor', 'accentColor']) {
    if (patch[key] !== undefined) {
      if (!isHexColor(patch[key])) return { ok: false, error: `${key} must be a 6-digit hex color like #336699.` };
      g[key] = patch[key].toLowerCase();
    }
  }
  return { ok: true, spec: next };
}

export function renameSignature(spec, name) {
  const trimmed = String(name || '').trim().slice(0, 80);
  if (!trimmed) return { ok: false, error: 'Name cannot be empty.' };
  const next = cloneSpec(spec);
  next.name = trimmed;
  return { ok: true, spec: next };
}

export function addRow(spec, { afterRowId, columnCount = 1, spacingTop = 8 } = {}) {
  if ((spec.rows || []).length >= LIMITS.maxRows) {
    return { ok: false, error: `Signatures support at most ${LIMITS.maxRows} rows.` };
  }
  const count = clamp(columnCount, 1, LIMITS.maxColumnsPerRow, 1);
  const next = cloneSpec(spec);
  const afterIdx = afterRowId ? next.rows.findIndex((r) => r.id === afterRowId) : next.rows.length - 1;
  if (afterRowId && afterIdx === -1) return { ok: false, error: `Row "${afterRowId}" does not exist.` };
  const row = createRow({ id: generateId('r', next), spacingTop: clamp(spacingTop, LIMITS.spacing.min, LIMITS.spacing.max, 8) });
  next.rows.splice(afterIdx + 1, 0, row);
  for (let i = 0; i < count; i += 1) {
    row.columns.push(createColumn({ id: generateId('c', next) }));
  }
  return { ok: true, spec: next, rowId: row.id, columnIds: row.columns.map((c) => c.id) };
}

export function removeRow(spec, rowId) {
  const next = cloneSpec(spec);
  const idx = next.rows.findIndex((r) => r.id === rowId);
  if (idx === -1) return { ok: false, error: `Row "${rowId}" does not exist.` };
  next.rows.splice(idx, 1);
  return { ok: true, spec: next };
}

export function updateColumn(spec, columnId, patch = {}) {
  const next = cloneSpec(spec);
  const found = findColumn(next, columnId);
  if (!found) return { ok: false, error: `Column "${columnId}" does not exist.` };
  const col = found.column;
  if (patch.width !== undefined) col.width = patch.width === null ? null : clamp(patch.width, 8, LIMITS.maxWidth.max, col.width);
  if (patch.valign !== undefined && ['top', 'middle', 'bottom'].includes(patch.valign)) col.valign = patch.valign;
  if (patch.align !== undefined && ['left', 'center', 'right'].includes(patch.align)) col.align = patch.align;
  if (patch.paddingLeft !== undefined) col.paddingLeft = clamp(patch.paddingLeft, LIMITS.spacing.min, LIMITS.spacing.max, col.paddingLeft);
  if (patch.paddingRight !== undefined) col.paddingRight = clamp(patch.paddingRight, LIMITS.spacing.min, LIMITS.spacing.max, col.paddingRight);
  if (patch.borderLeft !== undefined) {
    if (patch.borderLeft === null) {
      col.borderLeft = null;
    } else {
      const { color, thickness } = patch.borderLeft;
      if (!isHexColor(color)) return { ok: false, error: 'borderLeft.color must be a 6-digit hex color.' };
      col.borderLeft = { color: color.toLowerCase(), thickness: clamp(thickness, LIMITS.thickness.min, LIMITS.thickness.max, 2) };
    }
  }
  return { ok: true, spec: next };
}

/** Normalize + clamp a block patch (shared by add/update). Returns { ok, block | error }. */
function sanitizeBlock(type, raw, existing = null) {
  const b = { ...(existing || createBlock(type)), ...raw, type };
  const err = (msg) => ({ ok: false, error: msg });

  if (b.spacingTop !== undefined && b.spacingTop !== null) b.spacingTop = clamp(b.spacingTop, LIMITS.spacing.min, LIMITS.spacing.max, 0);

  switch (type) {
    case 'image': {
      b.src = normalizeImageSrc(b.src);
      b.alt = String(b.alt || 'Image').slice(0, 120);
      // null width/height = render at the image's natural size (used by
      // imported signatures whose HTML declared no explicit dimensions).
      b.width = b.width == null ? null : clamp(b.width, IMAGE_SANITIZE_MIN, LIMITS.imageSize.max, 96);
      b.height = b.height == null ? null : clamp(b.height, IMAGE_SANITIZE_MIN, LIMITS.imageSize.max, 96);
      b.circle = Boolean(b.circle);
      b.href = b.href ? normalizeHref('link', b.href) : '';
      if (!['left', 'center', 'right'].includes(b.align)) b.align = 'left';
      break;
    }
    case 'text': {
      b.text = String(b.text || '').slice(0, LIMITS.textLength);
      if (b.size != null) b.size = clamp(b.size, LIMITS.fontSize.min, LIMITS.fontSize.max, null);
      if (b.lineHeight != null) b.lineHeight = clamp(b.lineHeight, LIMITS.lineHeight.min, LIMITS.lineHeight.max, null);
      if (b.color && !isHexColor(b.color)) return err('text color must be a 6-digit hex color.');
      b.bold = Boolean(b.bold);
      b.italic = Boolean(b.italic);
      b.underline = Boolean(b.underline);
      b.href = b.href ? normalizeHref('link', b.href) : '';
      if (b.letterSpacing != null) b.letterSpacing = clamp(b.letterSpacing, 1, LIMITS.letterSpacing.max, null);
      // Rich inline runs (mixed bold/links/colors within one line). When
      // present they are the source of truth; block.text becomes the derived
      // plain concatenation (used by summaries + the text/plain clipboard).
      if (Array.isArray(b.segments) && b.segments.length) {
        b.segments = b.segments.slice(0, LIMITS.maxSegments).map((seg) => {
          const s = seg && typeof seg === 'object' ? seg : {};
          // Inline image segment (icon flowing with text, e.g. "📞 555-0123").
          if (s.image && typeof s.image === 'object') {
            const src = String(s.image.src || '').trim().replace(/^http:/i, 'https:');
            if (!/^https:\/\//i.test(src)) return null;
            const img = { src };
            img.width = s.image.width == null ? null
              : clamp(s.image.width, 1, LIMITS.imageSize.max, null);
            img.height = s.image.height == null ? null
              : clamp(s.image.height, 1, LIMITS.imageSize.max, null);
            if (s.image.href) {
              const href = normalizeHref('link', s.image.href);
              if (href) img.href = href;
            }
            if (s.image.alt) img.alt = String(s.image.alt).slice(0, 120);
            const out = { image: img };
            const pr = clamp(s.paddingRight, 0, 40, null);
            if (pr) out.paddingRight = pr;
            return out;
          }
          const out = {
            text: String(s.text || '').slice(0, LIMITS.segmentTextLength),
            bold: Boolean(s.bold),
            italic: Boolean(s.italic),
            underline: Boolean(s.underline),
          };
          if (s.color && isHexColor(s.color)) out.color = s.color.toLowerCase();
          if (s.size != null) {
            const n = clamp(s.size, LIMITS.fontSize.min, LIMITS.fontSize.max, null);
            if (n != null) out.size = n;
          }
          if (s.href) {
            const href = normalizeHref('link', s.href);
            if (href) out.href = href;
          }
          if (s.letterSpacing != null) {
            const ls = clamp(s.letterSpacing, 1, LIMITS.letterSpacing.max, null);
            if (ls != null) out.letterSpacing = ls;
          }
          const pr = clamp(s.paddingRight, 0, 40, null);
          if (pr) out.paddingRight = pr;
          return out;
        }).filter((s) => s && (s.image || s.text.length));
        if (b.segments.length) {
          b.text = b.segments.filter((s) => s.text != null).map((s) => s.text).join('').slice(0, LIMITS.textLength * 2);
        } else {
          delete b.segments;
        }
      } else if (b.segments !== undefined) {
        delete b.segments;
      }
      break;
    }
    case 'contact': {
      if (!Array.isArray(b.items)) b.items = [];
      b.items = b.items.slice(0, LIMITS.maxContactItems).map((item) => ({
        kind: CONTACT_KINDS.includes(item.kind) ? item.kind : 'text',
        label: String(item.label || item.value || '').slice(0, 120),
        value: String(item.value || '').slice(0, 200),
      })).filter((item) => item.label);
      b.separator = String(b.separator || ' | ').slice(0, 8);
      b.layout = b.layout === 'stacked' ? 'stacked' : 'inline';
      if (b.size != null) b.size = clamp(b.size, LIMITS.fontSize.min, LIMITS.fontSize.max, null);
      if (b.color && !isHexColor(b.color)) return err('contact color must be a 6-digit hex color.');
      break;
    }
    case 'social': {
      if (!Array.isArray(b.icons)) b.icons = [];
      const validNetworks = SOCIAL_NETWORKS.map((n) => n.id);
      b.icons = b.icons.slice(0, LIMITS.maxSocialIcons).map((icon) => ({
        network: validNetworks.includes(icon.network) ? icon.network : 'custom',
        label: String(icon.label || icon.network || 'Link').slice(0, 40),
        href: normalizeHref('link', icon.href),
        iconUrl: icon.iconUrl ? normalizeImageSrc(icon.iconUrl) : '',
      })).filter((icon) => icon.href);
      b.size = clamp(b.size, LIMITS.iconSize.min, LIMITS.iconSize.max, 20);
      b.gap = clamp(b.gap, 0, 24, 8);
      break;
    }
    case 'button': {
      b.text = String(b.text || 'Button').slice(0, 60);
      b.href = normalizeHref('link', b.href);
      if (b.bg && !isHexColor(b.bg)) return err('button bg must be a 6-digit hex color.');
      if (b.color && !isHexColor(b.color)) return err('button color must be a 6-digit hex color.');
      b.radius = clamp(b.radius, LIMITS.radius.min, LIMITS.radius.max, 4);
      b.paddingX = clamp(b.paddingX, 4, 40, 14);
      b.paddingY = clamp(b.paddingY, 2, 24, 8);
      b.size = clamp(b.size, LIMITS.fontSize.min, LIMITS.fontSize.max, 13);
      if (!['left', 'center', 'right'].includes(b.align)) b.align = 'left';
      break;
    }
    case 'divider': {
      if (b.color && !isHexColor(b.color)) return err('divider color must be a 6-digit hex color.');
      b.thickness = clamp(b.thickness, LIMITS.thickness.min, LIMITS.thickness.max, 1);
      b.spacingBottom = clamp(b.spacingBottom, LIMITS.spacing.min, LIMITS.spacing.max, 8);
      break;
    }
    case 'spacer': {
      b.height = clamp(b.height, 2, LIMITS.spacing.max, 8);
      break;
    }
    case 'disclaimer': {
      b.text = String(b.text || '').slice(0, LIMITS.textLength * 2);
      b.size = clamp(b.size, 8, 14, 10);
      if (b.color && !isHexColor(b.color)) return err('disclaimer color must be a 6-digit hex color.');
      if (b.lineHeight != null) b.lineHeight = clamp(b.lineHeight, LIMITS.lineHeight.min, LIMITS.lineHeight.max, null);
      break;
    }
    default:
      return err(`Unknown block type "${type}". Valid: ${BLOCK_TYPES.join(', ')}`);
  }
  return { ok: true, block: b };
}

export function addBlock(spec, { columnId, block, position } = {}) {
  if (!block || !BLOCK_TYPES.includes(block.type)) {
    return { ok: false, error: `block.type must be one of: ${BLOCK_TYPES.join(', ')}` };
  }
  const next = cloneSpec(spec);
  let col;
  if (columnId) {
    const found = findColumn(next, columnId);
    if (!found) return { ok: false, error: `Column "${columnId}" does not exist.` };
    col = found.column;
  } else {
    col = defaultColumn(next);
    if (!col) return { ok: false, error: 'The signature has no rows yet — call add_row first.' };
  }
  if (col.blocks.length >= LIMITS.maxBlocksPerColumn) {
    return { ok: false, error: `Columns support at most ${LIMITS.maxBlocksPerColumn} blocks.` };
  }
  const sanitized = sanitizeBlock(block.type, block);
  if (!sanitized.ok) return sanitized;
  const finalBlock = { ...sanitized.block, id: generateId('b', next) };
  const idx = position === undefined || position === null
    ? col.blocks.length
    : Math.min(Math.max(0, Math.round(position)), col.blocks.length);
  col.blocks.splice(idx, 0, finalBlock);
  return { ok: true, spec: next, blockId: finalBlock.id };
}

export function updateBlock(spec, blockId, patch = {}) {
  const next = cloneSpec(spec);
  const found = findBlock(next, blockId);
  if (!found) return { ok: false, error: `Block "${blockId}" does not exist. Call get_signature_state for current ids.` };
  const { type } = found.block;
  const sanitized = sanitizeBlock(type, patch, found.block);
  if (!sanitized.ok) return sanitized;
  found.column.blocks[found.index] = { ...sanitized.block, id: blockId, type };
  return { ok: true, spec: next };
}

export function removeBlock(spec, blockId) {
  const next = cloneSpec(spec);
  const found = findBlock(next, blockId);
  if (!found) return { ok: false, error: `Block "${blockId}" does not exist.` };
  found.column.blocks.splice(found.index, 1);
  return { ok: true, spec: next };
}

export function moveBlock(spec, { blockId, toColumnId, position } = {}) {
  const next = cloneSpec(spec);
  const found = findBlock(next, blockId);
  if (!found) return { ok: false, error: `Block "${blockId}" does not exist.` };
  let targetCol = found.column;
  if (toColumnId) {
    const foundCol = findColumn(next, toColumnId);
    if (!foundCol) return { ok: false, error: `Column "${toColumnId}" does not exist.` };
    targetCol = foundCol.column;
  }
  const [block] = found.column.blocks.splice(found.index, 1);
  const idx = position === undefined || position === null
    ? targetCol.blocks.length
    : Math.min(Math.max(0, Math.round(position)), targetCol.blocks.length);
  targetCol.blocks.splice(idx, 0, block);
  return { ok: true, spec: next };
}

/**
 * Validate + normalize a full incoming spec (from replace_signature or a
 * Firestore load). Rebuilds ids when missing. Returns { ok, spec | error }.
 */
export function sanitizeSpec(raw) {
  if (!raw || typeof raw !== 'object') return { ok: false, error: 'Spec must be an object.' };
  const spec = {
    version: 1,
    name: String(raw.name || 'My Signature').slice(0, 80),
    global: defaultGlobal(),
    rows: [],
  };
  const g = raw.global || {};
  const globalResult = updateGlobalStyle(spec, g);
  if (globalResult.ok) spec.global = globalResult.spec.global;

  const rows = Array.isArray(raw.rows) ? raw.rows.slice(0, LIMITS.maxRows) : [];
  for (const rawRow of rows) {
    const row = createRow({
      id: generateId('r', spec),
      spacingTop: clamp(rawRow.spacingTop, LIMITS.spacing.min, LIMITS.spacing.max, 0),
    });
    spec.rows.push(row);
    const cols = Array.isArray(rawRow.columns) ? rawRow.columns.slice(0, LIMITS.maxColumnsPerRow) : [];
    for (const rawCol of cols) {
      const col = createColumn({ id: generateId('c', spec) });
      row.columns.push(col);
      if (rawCol.width != null) col.width = clamp(rawCol.width, 8, LIMITS.maxWidth.max, null);
      if (['top', 'middle', 'bottom'].includes(rawCol.valign)) col.valign = rawCol.valign;
      if (['left', 'center', 'right'].includes(rawCol.align)) col.align = rawCol.align;
      col.paddingLeft = clamp(rawCol.paddingLeft, LIMITS.spacing.min, LIMITS.spacing.max, 0);
      col.paddingRight = clamp(rawCol.paddingRight, LIMITS.spacing.min, LIMITS.spacing.max, 0);
      if (rawCol.borderLeft && isHexColor(rawCol.borderLeft.color)) {
        col.borderLeft = {
          color: rawCol.borderLeft.color.toLowerCase(),
          thickness: clamp(rawCol.borderLeft.thickness, LIMITS.thickness.min, LIMITS.thickness.max, 2),
        };
      }
      const blocks = Array.isArray(rawCol.blocks) ? rawCol.blocks.slice(0, LIMITS.maxBlocksPerColumn) : [];
      for (const rawBlock of blocks) {
        if (!BLOCK_TYPES.includes(rawBlock.type)) continue;
        const sanitized = sanitizeBlock(rawBlock.type, rawBlock);
        if (sanitized.ok) {
          col.blocks.push({ ...sanitized.block, id: generateId('b', spec) });
        }
      }
    }
    if (!row.columns.length) row.columns.push(createColumn({ id: generateId('c', spec) }));
  }
  if (!spec.rows.length) {
    const row = createRow({ id: generateId('r', spec) });
    row.columns.push(createColumn({ id: generateId('c', spec) }));
    spec.rows.push(row);
  }
  return { ok: true, spec };
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

const COMPANY_NAME = 'First Heritage Lending';
const DEFAULT_DISCLAIMER = `${COMPANY_NAME} | Equal Housing Lender`;

function profileDefaults(profile = {}) {
  return {
    name: profile.name || 'Your Name',
    jobTitle: profile.jobTitle || 'Loan Officer',
    nmls: profile.nmls || '',
    phone: profile.phone || '',
    email: profile.email || '',
    website: profile.website || '',
    photoUrl: profile.photoUrl || '',
    logoUrl: profile.logoUrl || '',
  };
}

function titleLine(p) {
  return p.nmls ? `${p.jobTitle} | NMLS #${p.nmls}` : p.jobTitle;
}

function contactItems(p) {
  const items = [];
  if (p.phone) items.push({ kind: 'phone', label: p.phone, value: p.phone });
  if (p.email) items.push({ kind: 'email', label: p.email, value: p.email });
  return items;
}

function websiteItems(p) {
  return p.website ? [{ kind: 'website', label: p.website.replace(/^https?:\/\//i, ''), value: p.website }] : [];
}

function assembleSpec(name, buildRows) {
  const spec = { version: 1, name, global: defaultGlobal(), rows: [] };
  buildRows(spec);
  return spec;
}

function pushRow(spec, { spacingTop = 0, columns }) {
  const row = createRow({ id: generateId('r', spec), spacingTop });
  spec.rows.push(row);
  for (const colDef of columns) {
    const col = createColumn({ ...colDef, id: generateId('c', spec), blocks: [] });
    row.columns.push(col);
    for (const blockDef of colDef.blocks || []) {
      const sanitized = sanitizeBlock(blockDef.type, blockDef);
      if (sanitized.ok) col.blocks.push({ ...sanitized.block, id: generateId('b', spec) });
    }
  }
  return row;
}

function detailBlocks(p, { includeWebsite = true } = {}) {
  const blocks = [
    createBlock('text', { text: p.name, bold: true, size: 16, color: '#222222' }),
    createBlock('text', { text: titleLine(p), size: 13, color: '#666666', spacingTop: 2 }),
    createBlock('contact', { items: contactItems(p), spacingTop: 8 }),
  ];
  if (includeWebsite && websiteItems(p).length) {
    blocks.push(createBlock('contact', { items: websiteItems(p), spacingTop: 2 }));
  }
  return blocks;
}

export const TEMPLATES = [
  {
    id: 'classic-headshot',
    name: 'Classic — headshot left',
    description: 'Circular headshot on the left, name/title/contact details on the right. The canonical loan-officer signature.',
    build(profile) {
      const p = profileDefaults(profile);
      return assembleSpec('Classic', (spec) => {
        pushRow(spec, {
          columns: [
            {
              width: 104,
              paddingRight: 16,
              blocks: p.photoUrl ? [createBlock('image', { src: p.photoUrl, alt: p.name, width: 88, height: 88, circle: true })] : [],
            },
            { blocks: detailBlocks(p) },
          ],
        });
      });
    },
  },
  {
    id: 'logo-left',
    name: 'Logo left',
    description: 'Company logo on the left, details on the right, small disclaimer underneath.',
    build(profile) {
      const p = profileDefaults(profile);
      return assembleSpec('Logo Left', (spec) => {
        pushRow(spec, {
          columns: [
            {
              width: 126,
              paddingRight: 18,
              valign: 'middle',
              blocks: p.logoUrl ? [createBlock('image', { src: p.logoUrl, alt: COMPANY_NAME, width: 110, height: 110 })] : [],
            },
            { blocks: detailBlocks(p) },
          ],
        });
        pushRow(spec, {
          spacingTop: 10,
          columns: [{
            blocks: [
              createBlock('divider', { spacingTop: 0, spacingBottom: 6 }),
              createBlock('disclaimer', { text: DEFAULT_DISCLAIMER, spacingTop: 0 }),
            ],
          }],
        });
      });
    },
  },
  {
    id: 'stacked-centered',
    name: 'Stacked & centered',
    description: 'Everything centered in a single column: photo, name, title, contacts. Very mobile-friendly.',
    build(profile) {
      const p = profileDefaults(profile);
      return assembleSpec('Centered', (spec) => {
        pushRow(spec, {
          columns: [{
            align: 'center',
            blocks: [
              ...(p.photoUrl ? [createBlock('image', { src: p.photoUrl, alt: p.name, width: 80, height: 80, circle: true, align: 'center' })] : []),
              createBlock('text', { text: p.name, bold: true, size: 16, color: '#222222', spacingTop: 8 }),
              createBlock('text', { text: titleLine(p), size: 12, color: '#666666', spacingTop: 2 }),
              createBlock('contact', { items: [...contactItems(p), ...websiteItems(p)], spacingTop: 8 }),
            ],
          }],
        });
      });
    },
  },
  {
    id: 'accent-bar',
    name: 'Accent bar',
    description: 'Headshot beside a vertical accent-colored bar with the details to its right. Modern look.',
    build(profile) {
      const p = profileDefaults(profile);
      return assembleSpec('Accent Bar', (spec) => {
        pushRow(spec, {
          columns: [
            {
              width: 96,
              paddingRight: 16,
              valign: 'middle',
              blocks: p.photoUrl ? [createBlock('image', { src: p.photoUrl, alt: p.name, width: 80, height: 80, circle: true })] : [],
            },
            {
              paddingLeft: 16,
              borderLeft: { color: '#0f4c81', thickness: 3 },
              blocks: detailBlocks(p),
            },
          ],
        });
      });
    },
  },
  {
    id: 'minimal-text',
    name: 'Minimal text',
    description: 'No images — clean stacked text with a one-line contact row. Never breaks anywhere.',
    build(profile) {
      const p = profileDefaults(profile);
      return assembleSpec('Minimal', (spec) => {
        pushRow(spec, {
          columns: [{
            blocks: [
              createBlock('text', { text: p.name, bold: true, size: 15, color: '#222222' }),
              createBlock('text', { text: titleLine(p), size: 12, color: '#666666', spacingTop: 1 }),
              createBlock('contact', { items: [...contactItems(p), ...websiteItems(p)], spacingTop: 6 }),
            ],
          }],
        });
      });
    },
  },
  {
    id: 'full-banner',
    name: 'Full — banner + disclaimer',
    description: 'Headshot and details up top, a divider, a clickable promo banner, and a compliance disclaimer.',
    build(profile) {
      const p = profileDefaults(profile);
      return assembleSpec('Full', (spec) => {
        pushRow(spec, {
          columns: [
            {
              width: 104,
              paddingRight: 16,
              blocks: p.photoUrl ? [createBlock('image', { src: p.photoUrl, alt: p.name, width: 88, height: 88, circle: true })] : [],
            },
            {
              blocks: [
                ...detailBlocks(p),
                createBlock('button', { text: 'Apply Now', href: p.website || '', spacingTop: 10 }),
              ],
            },
          ],
        });
        pushRow(spec, {
          spacingTop: 12,
          columns: [{
            blocks: [
              createBlock('divider', { spacingTop: 0, spacingBottom: 8 }),
              createBlock('disclaimer', { text: DEFAULT_DISCLAIMER + (p.nmls ? `\nNMLS #${p.nmls}` : ''), spacingTop: 0 }),
            ],
          }],
        });
      });
    },
  },
];

export const TEMPLATE_IDS = TEMPLATES.map((t) => t.id);

export function buildTemplateSpec(templateId, profile) {
  const template = TEMPLATES.find((t) => t.id === templateId);
  if (!template) return { ok: false, error: `Unknown template "${templateId}". Valid: ${TEMPLATE_IDS.join(', ')}` };
  return { ok: true, spec: template.build(profile || {}) };
}

export function blankSpec(profile) {
  return buildTemplateSpec('minimal-text', profile).spec;
}
